/**
 * One look for every form field in the app's entry sheets — the look the
 * workout logger established (Sept 2026), applied to ordinary MUI TextField /
 * Select / DatePicker markup WITHOUT touching each field:
 *
 *   LABEL (small, monospace, uppercase, muted) sits ABOVE the field
 *   the field is the app's standard 48px card-fill pill from global.css
 *   the value is monospace bold; units and icons ride inside as adornments
 *   no floating label, no notched outline, no legend gap in the border
 *
 * Spread it on the container that holds the fields (DIALOG_STYLES.content
 * does this for every dialog; SmartFoodEntry does it for the Log Food sheet).
 * Anything that already renders labels with `Txt` above an InputBase (the
 * exercise screens) is unaffected.
 *
 * Two traps this file works around, so nobody re-learns them:
 *  - MUI sx multiplies bare numbers on padding/margin by the 8px spacing unit
 *    (`paddingLeft: 12` is 96px). Every length here is a px string.
 *  - global.css paints the shrunk floating label with the card fill and a
 *    green focus border, both `!important`. The rules below out-rank them
 *    with `!important` at higher specificity (`&&` doubles the class).
 */
const mono = 'var(--font-mono)';

export const entryFormSx = {
  '& .MuiFormControl-root': { width: '100%' },
  // The floating label becomes a static caption above the input.
  '&& .MuiInputLabel-root, && .MuiInputLabel-root.MuiInputLabel-shrink, && .MuiFormLabel-root': {
    position: 'static',
    transform: 'none',
    maxWidth: 'none',
    pointerEvents: 'auto',
    fontFamily: mono,
    fontSize: '0.6rem',
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.55) !important',
    marginBottom: '4px',
    background: 'transparent !important',
    padding: '0 !important',
    lineHeight: 1.4,
    '& .MuiFormLabel-asterisk': { color: '#FFD740' },
  },
  '&& .MuiInputLabel-root.Mui-focused': { color: '#00E0FF !important' },
  // global.css already draws the field (card fill, light border, 12px); the
  // notched outline would draw a second border with a gap for the label.
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
  '& .MuiInputBase-root': {
    minHeight: '48px',
    paddingLeft: '12px',
    paddingRight: '12px',
    paddingTop: 0,
    paddingBottom: 0,
    fontFamily: mono,
    fontSize: '1rem',
    fontWeight: 700,
    color: '#fff',
  },
  // Focus is the family's cyan, not the global green.
  '&& .MuiInputBase-root.Mui-focused': {
    borderColor: '#00E0FF !important',
    boxShadow: '0 0 0 1px rgba(0,224,255,0.25) !important',
  },
  '& .MuiInputBase-root.MuiInputBase-multiline': { minHeight: 0, paddingTop: '10px', paddingBottom: '10px' },
  '& .MuiInputBase-input': {
    padding: '0 !important',
    height: 'auto',
    fontFamily: mono,
    fontSize: '1rem',
    fontWeight: 700,
    '&::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1, fontWeight: 500 },
  },
  '& .MuiInputBase-multiline .MuiInputBase-input': { fontWeight: 500, fontSize: '0.9rem', lineHeight: 1.5 },
  // display:block, not flex — a flex select clips its text at the border edge, so a long
  // value ran under the arrow instead of ending in an ellipsis. The root is already a
  // centred flex row, so the block value still sits vertically centred.
  '& .MuiSelect-select': { padding: '0 !important', paddingRight: '28px !important', display: 'block', minHeight: '0 !important', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  '& .MuiSelect-icon': { color: 'rgba(0,224,255,0.7)', right: '8px' },
  '& .MuiInputAdornment-root': { color: 'rgba(255,255,255,0.5)', fontFamily: mono, fontSize: '0.7rem' },
  '& .MuiFormHelperText-root': { fontFamily: mono, fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', marginLeft: 0, marginTop: '4px' },
  '& .MuiCheckbox-root': { color: 'rgba(0,224,255,0.45)', '&.Mui-checked': { color: '#00E0FF' } },
  '& .MuiFormControlLabel-label': { fontFamily: mono, fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)' },
  // Section captions written as plain Typography inside a form read as the
  // family's cyan section label (the logger's ACTIVITY / DURATION / NAME).
  '& .entry-section': { fontFamily: mono, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: '#00E0FF', marginTop: '8px', marginBottom: '6px' },
} as const;

/**
 * A nested item inside an entry sheet — one biomarker in the bloodwork form,
 * one food in a meal — drawn as a quiet bordered group so a long list of
 * fields still reads as rows.
 */
export const entryGroupSx = {
  border: '1px solid rgba(0,224,255,0.12)',
  borderRadius: '12px',
  p: 1.5,
  mb: 1.5,
} as const;

/** 48px square icon button that sits level with a 48px field. */
export const entryIconButtonSx = {
  width: 48,
  height: 48,
  flexShrink: 0,
  borderRadius: '12px',
  color: '#00E0FF',
  bgcolor: 'rgba(0,224,255,0.06)',
  border: '1px solid rgba(0,224,255,0.2)',
  '&:hover': { bgcolor: 'rgba(0,224,255,0.12)' },
} as const;
